/**
 * LYT Communications - Client Invoice Page (Vexus Billing)
 * Version: 1.0.0
 * Created: 2026-02-23
 *
 * Admin generates invoices TO Vexus at Vexus rates.
 * Track submission, payment received, client retainage.
 * Links to P&L for full cash flow visibility.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowLeft, DollarSign, Plus, FileText, CheckCircle, Clock, Send, Loader, RefreshCw, Download } from 'lucide-react';
import { loadFullProject, loadInvoices, readSheet, appendRow, writeSheet, fetchFullRates } from '../services/mapService';

const DB_MAP_SS = '14cHm7jvCHLRhMDPpiF-J8PaGgiFMxsYFlwfr49Wwcik';

const CLIENT_INVOICE_HEADERS = [
  'invoice_id','project_id','invoice_number','status','line_items_json',
  'subtotal','retainage_pct','retainage_amount','net_amount',
  'submitted_date','paid_date','paid_amount','check_number',
  'vexus_retainage_status','vexus_retainage_released_date','notes','created_by','created_date',
];

function ClientInvoicePage({ darkMode, user, setCurrentPage }) {
  const [segments, setSegments] = useState([]);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'create'
  const [selectedSegments, setSelectedSegments] = useState(new Set());
  const [customItems, setCustomItems] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const bgColor = darkMode ? '#0d1b2a' : '#f8fafc';
  const cardBg = darkMode ? '#1e293b' : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';
  const accent = darkMode ? '#667eea' : '#0077B6';

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Client billing rates — fetched from server (key-gated), never in the bundle
  const [fullRateInfo, setFullRateInfo] = useState(null);
  useEffect(() => {
    let live = true;
    fetchFullRates('vexus-la-tx-2026')
      .then(d => { if (live && d) setFullRateInfo(d); })
      .catch(err => console.error('Failed to load rates:', err));
    return () => { live = false; };
  }, []);
  const vexusRates = fullRateInfo?.rates || {};

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await loadFullProject(null);
      setSegments(data?.segments || []);
      const invs = await loadClientInvoices();
      setClientInvoices(invs);
    } catch (err) {
      console.error('[ClientInvoice] Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadClientInvoices = async () => {
    try {
      const data = await readSheet(DB_MAP_SS, 'ClientInvoices!A1:R1000');
      if (!data || data.length < 2) return [];
      const headers = data[0];
      return data.slice(1).map(row => {
        const obj = {};
        headers.forEach((h, i) => { obj[h] = row[i] || ''; });
        return obj;
      });
    } catch (e) { return []; }
  };

  // QC-approved segments not yet invoiced to Vexus
  const billableSegments = useMemo(() => {
    const invoicedIds = new Set();
    clientInvoices.forEach(inv => {
      try {
        const items = JSON.parse(inv.line_items_json || '[]');
        items.forEach(item => { if (item.segment_id) invoicedIds.add(item.segment_id); });
      } catch (e) {}
    });
    return segments.filter(seg => {
      if (invoicedIds.has(seg.segment_id)) return false;
      return seg.boring_status === 'QC Approved' || seg.pulling_status === 'QC Approved';
    });
  }, [segments, clientInvoices]);

  const generateLineItems = useCallback(() => {
    const items = [];
    billableSegments.filter(s => selectedSegments.has(s.segment_id)).forEach(seg => {
      const footage = parseFloat(seg.footage) || 0;
      const actualFootage = parseFloat(seg.boring_actual_footage) || footage;

      if (seg.boring_status === 'QC Approved') {
        const rate = vexusRates?.UG1?.vexus || 0;
        items.push({
          segment_id: seg.segment_id,
          description: `Boring — ${seg.street || seg.segment_id} (${actualFootage} LF)`,
          code: 'UG1',
          qty: actualFootage,
          rate,
          total: actualFootage * rate,
        });
      }
      if (seg.pulling_status === 'QC Approved') {
        const ct = parseInt(seg.pulling_cable_type) || 24;
        const code = ct > 144 ? 'UG28' : 'UG4';
        const rate = vexusRates?.[code]?.vexus || 0;
        items.push({
          segment_id: seg.segment_id,
          description: `Pulling ${ct}ct — ${seg.street || seg.segment_id} (${footage} LF)`,
          code,
          qty: footage,
          rate,
          total: footage * rate,
        });
      }
    });
    return [...items, ...customItems];
  }, [billableSegments, selectedSegments, customItems, vexusRates]);

  const allLineItems = generateLineItems();
  const subtotal = allLineItems.reduce((sum, li) => sum + (li.total || 0), 0);
  const retainagePct = 10;
  const retainageAmount = subtotal * (retainagePct / 100);
  const netAmount = subtotal - retainageAmount;

  const handleSave = async (submit = false) => {
    if (allLineItems.length === 0) return;
    setSaving(true);
    try {
      const invId = `CINV-${Date.now().toString(36).toUpperCase()}`;
      const row = CLIENT_INVOICE_HEADERS.map(h => {
        if (h === 'invoice_id') return invId;
        if (h === 'invoice_number') return invoiceNumber || invId;
        if (h === 'status') return submit ? 'Submitted' : 'Draft';
        if (h === 'line_items_json') return JSON.stringify(allLineItems);
        if (h === 'subtotal') return String(subtotal.toFixed(2));
        if (h === 'retainage_pct') return String(retainagePct);
        if (h === 'retainage_amount') return String(retainageAmount.toFixed(2));
        if (h === 'net_amount') return String(netAmount.toFixed(2));
        if (h === 'submitted_date') return submit ? new Date().toISOString() : '';
        if (h === 'vexus_retainage_status') return 'Held';
        if (h === 'notes') return notes;
        if (h === 'created_by') return user?.email || '';
        if (h === 'created_date') return new Date().toISOString();
        return '';
      });
      await appendRow(DB_MAP_SS, 'ClientInvoices', [row]);
      await loadData();
      setView('list');
      setSelectedSegments(new Set());
      setCustomItems([]);
      setInvoiceNumber('');
      setNotes('');
    } catch (err) {
      console.error('[ClientInvoice] Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async (inv) => {
    try {
      const data = await readSheet(DB_MAP_SS, 'ClientInvoices!A1:R1000');
      if (!data || data.length < 2) return;
      const headers = data[0];
      const statusCol = headers.indexOf('status');
      const paidDateCol = headers.indexOf('paid_date');
      for (let i = 1; i < data.length; i++) {
        if (data[i][0] === inv.invoice_id) {
          const statusLetter = String.fromCharCode(65 + statusCol);
          const paidLetter = String.fromCharCode(65 + paidDateCol);
          await writeSheet(DB_MAP_SS, `ClientInvoices!${statusLetter}${i + 1}`, [['Paid']]);
          await writeSheet(DB_MAP_SS, `ClientInvoices!${paidLetter}${i + 1}`, [[new Date().toISOString()]]);
          break;
        }
      }
      await loadData();
    } catch (err) {
      console.error('[ClientInvoice] markPaid error:', err);
    }
  };

  const formatCurrency = (amt) => `$${(parseFloat(amt) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const STATUS_COLORS = { 'Draft': '#9E9E9E', 'Submitted': '#FFB800', 'Paid': '#4CAF50' };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size={32} color={accent} style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor, padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => setCurrentPage('admin-dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textColor, padding: 4 }}>
          <ArrowLeft size={24} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: textColor, margin: 0 }}>Client Invoicing</h1>
          <p style={{ color: mutedColor, fontSize: '0.85rem', margin: 0 }}>Invoice Vexus at contract rates</p>
        </div>
        <button onClick={() => setCurrentPage('pl-dashboard')} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${borderColor}`, background: cardBg, color: textColor, fontSize: 13, cursor: 'pointer' }}>
          <DollarSign size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> P&L
        </button>
        <button onClick={loadData} style={{ padding: 8, background: 'none', border: `1px solid ${borderColor}`, borderRadius: 8, cursor: 'pointer', color: textColor }}>
          <RefreshCw size={18} />
        </button>
      </div>

      {view === 'list' && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
            {[
              { label: 'Total Invoiced', value: formatCurrency(clientInvoices.reduce((s, i) => s + (parseFloat(i.subtotal) || 0), 0)), color: accent },
              { label: 'Paid', value: formatCurrency(clientInvoices.filter(i => i.status === 'Paid').reduce((s, i) => s + (parseFloat(i.paid_amount || i.net_amount) || 0), 0)), color: '#4CAF50' },
              { label: 'Pending', value: formatCurrency(clientInvoices.filter(i => i.status === 'Submitted').reduce((s, i) => s + (parseFloat(i.net_amount) || 0), 0)), color: '#FFB800' },
              { label: 'Retainage Held', value: formatCurrency(clientInvoices.filter(i => i.vexus_retainage_status === 'Held').reduce((s, i) => s + (parseFloat(i.retainage_amount) || 0), 0)), color: '#FF9800' },
            ].map((stat, idx) => (
              <div key={idx} style={{ background: cardBg, borderRadius: 12, padding: 20 }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
                <div style={{ fontSize: '0.85rem', color: mutedColor }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* New invoice button */}
          <button onClick={() => setView('create')} style={{ marginBottom: 20, padding: '10px 20px', borderRadius: 8, background: accent, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={16} /> New Client Invoice
          </button>

          {/* Invoice list */}
          {clientInvoices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: mutedColor, background: cardBg, borderRadius: 12 }}>
              <FileText size={40} style={{ opacity: 0.3, marginBottom: 8 }} />
              <div>No client invoices yet.</div>
              <div style={{ fontSize: '0.85rem', marginTop: 4 }}>Create an invoice from QC-approved work to bill Vexus.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {clientInvoices.map(inv => (
                <div key={inv.invoice_id} style={{ background: cardBg, borderRadius: 12, padding: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, color: textColor }}>{inv.invoice_number || inv.invoice_id}</span>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, fontWeight: 600, background: `${STATUS_COLORS[inv.status] || '#9E9E9E'}20`, color: STATUS_COLORS[inv.status] || '#9E9E9E' }}>{inv.status}</span>
                    </div>
                    <div style={{ fontSize: 13, color: mutedColor }}>{inv.project_id} | Created {new Date(inv.created_date).toLocaleDateString()}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: accent }}>{formatCurrency(inv.subtotal)}</div>
                    <div style={{ fontSize: 12, color: mutedColor }}>Net: {formatCurrency(inv.net_amount)}</div>
                    {inv.status === 'Submitted' && (
                      <button onClick={() => handleMarkPaid(inv)} style={{ marginTop: 4, padding: '4px 10px', fontSize: 11, fontWeight: 600, background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                        <CheckCircle size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Mark Paid
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Billable segments count */}
          {billableSegments.length > 0 && (
            <div style={{ marginTop: 16, fontSize: 13, color: mutedColor }}>
              {billableSegments.length} QC-approved segment(s) available for billing.
            </div>
          )}
        </>
      )}

      {view === 'create' && (
        <div style={{ background: cardBg, borderRadius: 12, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', color: textColor }}>New Client Invoice (Vexus)</h2>
            <button onClick={() => setView('list')} style={{ background: 'none', border: `1px solid ${borderColor}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: textColor, fontSize: 13 }}>Cancel</button>
          </div>

          {/* Invoice number */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, color: mutedColor, display: 'block', marginBottom: 4 }}>Invoice Number</label>
            <input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="Auto-generated if blank" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${borderColor}`, background: bgColor, color: textColor, fontSize: 14, boxSizing: 'border-box' }} />
          </div>

          {/* Select segments */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, color: mutedColor, display: 'block', marginBottom: 8 }}>Select QC-Approved Segments to Invoice</label>
            {billableSegments.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: mutedColor, fontSize: 13 }}>No billable segments available.</div>
            ) : (
              <div style={{ maxHeight: 250, overflowY: 'auto', border: `1px solid ${borderColor}`, borderRadius: 8 }}>
                <div style={{ padding: '8px 12px', borderBottom: `1px solid ${borderColor}`, display: 'flex', gap: 8 }}>
                  <button onClick={() => setSelectedSegments(new Set(billableSegments.map(s => s.segment_id)))} style={{ fontSize: 11, color: accent, background: 'none', border: 'none', cursor: 'pointer' }}>Select All</button>
                  <button onClick={() => setSelectedSegments(new Set())} style={{ fontSize: 11, color: mutedColor, background: 'none', border: 'none', cursor: 'pointer' }}>Clear</button>
                </div>
                {billableSegments.map(seg => (
                  <label key={seg.segment_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: `1px solid ${borderColor}`, cursor: 'pointer' }}>
                    <input type="checkbox" checked={selectedSegments.has(seg.segment_id)} onChange={() => {
                      setSelectedSegments(prev => {
                        const next = new Set(prev);
                        if (next.has(seg.segment_id)) next.delete(seg.segment_id);
                        else next.add(seg.segment_id);
                        return next;
                      });
                    }} />
                    <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: textColor }}>{seg.contractor_id || seg.segment_id}</span>
                    <span style={{ fontSize: 12, color: mutedColor }}>{seg.street} | {seg.footage} LF</span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                      {seg.boring_status === 'QC Approved' && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 6, background: '#4CAF5020', color: '#4CAF50' }}>Bore</span>}
                      {seg.pulling_status === 'QC Approved' && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 6, background: '#2196F320', color: '#2196F3' }}>Pull</span>}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Line items preview */}
          {allLineItems.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, color: textColor, marginBottom: 8 }}>Line Items ({allLineItems.length})</h3>
              <div style={{ border: `1px solid ${borderColor}`, borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: bgColor }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: mutedColor }}>Description</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', color: mutedColor }}>Qty</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', color: mutedColor }}>Rate</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', color: mutedColor }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allLineItems.map((li, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${borderColor}` }}>
                        <td style={{ padding: '8px 12px', color: textColor }}>{li.description}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: textColor }}>{li.qty.toLocaleString()}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: mutedColor }}>${li.rate.toFixed(2)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: textColor }}>{formatCurrency(li.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Totals */}
          <div style={{ background: bgColor, borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: mutedColor }}>Subtotal</span>
              <span style={{ fontWeight: 600, color: textColor }}>{formatCurrency(subtotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: mutedColor }}>Retainage ({retainagePct}%)</span>
              <span style={{ fontWeight: 600, color: '#FF9800' }}>-{formatCurrency(retainageAmount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: `1px solid ${borderColor}` }}>
              <span style={{ fontWeight: 700, color: textColor }}>Net Payable</span>
              <span style={{ fontWeight: 700, fontSize: '1.25rem', color: accent }}>{formatCurrency(netAmount)}</span>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, color: mutedColor, display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Optional notes..." style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${borderColor}`, background: bgColor, color: textColor, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }} />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => handleSave(false)} disabled={allLineItems.length === 0 || saving}
              style={{ flex: 1, padding: 12, borderRadius: 8, border: `1px solid ${borderColor}`, background: cardBg, color: textColor, fontSize: 14, fontWeight: 600, cursor: allLineItems.length ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Clock size={16} /> Save Draft
            </button>
            <button onClick={() => handleSave(true)} disabled={allLineItems.length === 0 || saving}
              style={{ flex: 1, padding: 12, borderRadius: 8, border: 'none', background: allLineItems.length ? accent : '#9E9E9E', color: '#fff', fontSize: 14, fontWeight: 600, cursor: allLineItems.length ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              {saving ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />} Submit to Vexus
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default ClientInvoicePage;
